import React from 'react';
import {
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
  Chip,
  useTheme,
  alpha,
} from '@mui/material';
import { 
  Print as PrintIcon, 
  TrendingUp as TrendingUpIcon,
  Receipt as ReceiptIcon,
  SwapHoriz as SwapHorizIcon 
} from '@mui/icons-material';
import { format } from 'date-fns';
import { FinancialSettlementReport as FinancialSettlementReportType } from '../../types';

interface FinancialSettlementReportProps {
  report: FinancialSettlementReportType;
  onPrint?: () => void;
  selectedBranchName?: string;
}

const FinancialSettlementReport: React.FC<FinancialSettlementReportProps> = ({
  report,
  onPrint,
  selectedBranchName
}) => {
  const theme = useTheme();
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-EG', { 
      style: 'currency', 
      currency: 'EGP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2 
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-EG').format(num);
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'EEEE, MMMM dd, yyyy');
    } catch {
      return dateString;
    }
  };

  const formatPercentage = (rate: number) => {
    return `${(rate * 100).toFixed(2)}%`;
  };

  const handlePrint = () => {
    if (onPrint) {
      onPrint();
    } else {
      window.print();
    }
  };

  const { settlementSummary, cashOrdersBreakdown, onlineOrdersBreakdown } = report;

  return (
    <div className="print-area">
      {/* Unified Print & Screen Styles */}
      <style>
        {`
          /* Common styles for both print and screen */
          .print-area {
            width: 100%;
            max-width: 100vw;
            margin: 0;
            padding: 0;
            background: ${theme.palette.background.paper};
            font-family: 'Arial', sans-serif;
            font-size: 14px;
            line-height: 1.4;
            color: ${theme.palette.text.primary};
            min-height: 100vh;
            box-sizing: border-box;
            overflow-x: auto;
          }
          
          /* Print view should always use light theme colors regardless of user's theme preference */
          @media print {
            .print-area {
              background-color: white !important;
              color: black !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            
            .report-container {
              background-color: white !important;
              color: black !important;
            }
            
            /* Force white background for all report containers and cards */
            .print-area, .report-container, .settlement-card, .breakdown-section, .print-section,
            .settlement-content, .section-content, .settlement-summary-section {
              background-color: white !important;
              border-color: #cccccc !important;
              color: black !important;
            }
            
            /* Force all divs inside these containers to have white background */
            .print-area div, .report-container div, .settlement-card div, 
            .breakdown-section div, .print-section div {
              background-color: white !important;
            }
            
            .print-area .MuiTableHead-root th, 
            .print-area th {
              background-color: #f5f5f5 !important;
              color: black !important;
              border-color: #cccccc !important;
            }
            
            .print-area .MuiTableCell-root,
            .print-area td {
              background-color: white !important;
              color: black !important;
              border-color: #cccccc !important;
            }
            
            .print-area .net-settlement-row td {
              background-color: #f0f0f0 !important;
            }
            
            .print-area .settlement-card {
              background-color: white !important;
              border-color: #cccccc !important;
              box-shadow: none !important;
            }
          }
          
          .report-container {
            padding: 20px;
            background: ${theme.palette.background.paper};
          }
          
          /* Section styling like Sales Summary Report */
          .breakdown-section, .settlement-card, .settlement-summary {
            margin-bottom: 30px;
            padding: 0;
            background: transparent;
            border: none;
            box-shadow: none;
          }
          
          .section-content, .settlement-content {
            padding: 0;
            background: transparent;
          }
          
          /* Table styling for screen view - like Sales Summary Report */
          .print-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 30px;
            font-size: 14px;
            page-break-inside: avoid;
            table-layout: auto;
          }
          
          .print-table th {
            background-color: ${theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.action.hover};
            font-weight: bold;
            padding: 15px 10px;
            border: 1px solid ${theme.palette.divider};
            border-bottom: 2px solid ${theme.palette.divider};
            text-align: left;
            font-size: 14px;
            word-wrap: break-word;
            color: ${theme.palette.text.primary};
          }
          
          .print-table td {
            padding: 12px 8px;
            border: 1px solid ${theme.palette.divider};
            font-size: 13px;
            word-wrap: break-word;
            text-align: left;
            color: ${theme.palette.text.primary};
            background: ${theme.palette.background.paper};
          }
          
          /* Net Settlement row styling for screen view */
          .MuiTableBody-root .net-settlement-row .MuiTableCell-root {
            background-color: ${theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#f0f0f0'} !important;
            border-top: 2px solid ${theme.palette.divider} !important;
          }
          
          tr.net-settlement-row td {
            background-color: ${theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#f0f0f0'} !important;
            border-top: 2px solid ${theme.palette.divider} !important;
          }
          
          .print-table .number {
            text-align: right;
          }
          
          /* Settlement summary styling with more visual emphasis */
          .settlement-card {
            border: 1px solid ${theme.palette.divider} !important;
            padding: 20px;
            margin-bottom: 30px;
            background: ${theme.palette.mode === 'dark' ? 'transparent' : theme.palette.background.paper};
            border-radius: 8px !important;
            box-shadow: 0 2px 8px ${theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.1)'} !important;
          }
          
          /* Remove borders from breakdown sections - make them clean like print view */
          .breakdown-section,
          .cash-orders-breakdown,
          .online-orders-breakdown,
          .settlement-summary-section {
            border: none !important;
            padding: 0 !important;
            background: transparent !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            margin-bottom: 30px;
          }
          
          .report-container {
            padding: 20px;
            background: ${theme.palette.background.paper};
            min-height: calc(100vh - 100px);
          }
          
          /* Make page view match print view - clean sections with only table borders */
          .section-content, .settlement-content {
            padding: 0;
            background: transparent;
          }
          
          /* Numbered section headers with underline */
          .section-number {
            display: inline-block;
            width: 24px;
            height: 24px;
            background-color: ${theme.palette.primary.main};
            color: white;
            border-radius: 50%;
            text-align: center;
            line-height: 24px;
            font-size: 14px;
            font-weight: bold;
            margin-right: 12px;
          }
          
          .section-title-with-number {
            display: flex;
            align-items: center;
            margin-bottom: 16px;
            padding-bottom: 8px;
            border-bottom: 2px solid ${theme.palette.divider};
          }
          
          /* Print-specific adjustments */
          @media print {
            @page {
              margin: 5mm;
              size: A4 portrait;
            }
            
            /* Reset everything for print */
            * {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
              print-color-adjust: exact !important;
              box-sizing: border-box !important;
            }
            
            /* Hide UI elements */
            .no-print {
              display: none !important;
            }
            
            /* Hide navigation and sidebar elements */
            nav, .sidebar, .menu, .navigation, .drawer, .left-panel, 
            [class*="sidebar"], [class*="menu"], [class*="nav"], 
            [class*="drawer"], [role="navigation"] {
              display: none !important;
            }
            
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              background: white !important;
              width: 100% !important;
              height: auto !important;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
            }
            
            /* Print container */
            .print-area {
              width: 100% !important;
              max-width: none !important;
              margin: 0 !important;
              padding: 0 !important;
              background: white !important;
              overflow: visible !important;
              height: auto !important;
            }
            
            /* Target the Paper component for printing only */
            .print-area .MuiPaper-root {
              width: 100% !important;
              max-width: none !important;
              margin: 0 !important;
              padding: 2mm !important;
              background: white !important;
              box-shadow: none !important;
              border: none !important;
              border-radius: 0 !important;
              overflow: visible !important;
              height: auto !important;
              min-height: auto !important;
              max-height: none !important;
              font-size: 12px !important;
              line-height: 1.4 !important;
              color: black !important;
              page-break-inside: auto !important;
            }
            
            .print-area {
              width: 100% !important;
              max-width: none !important;
              margin: 0 !important;
              padding: 0 !important;
              background: white !important;
              box-shadow: none !important;
              border: none !important;
              overflow: visible !important;
              height: auto !important;
              min-height: auto !important;
              max-height: none !important;
              font-size: 12px !important;
              line-height: 1.3 !important;
              color: black !important;
              page-break-inside: auto !important;
              page-break-before: auto !important;
              page-break-after: auto !important;
            }
            
            .no-print {
              display: none !important;
            }
            
            .print-header {
              margin-bottom: 5mm !important;
              border-bottom: 1px solid black !important;
              padding: 2mm 0 2mm 0 !important;
              page-break-after: avoid !important;
              color: black !important;
              background-color: white !important;
            }
            
            /* Targeted fix for multi-line text in first column table cells only */
            .print-area .MuiTableBody-root .MuiTableCell-root:first-child,
            .print-area tbody td:first-child {
              overflow: visible !important;
              white-space: normal !important;
              max-height: none !important;
              height: auto !important;
              padding: 2.5mm 1.5mm !important;
            }
            
            /* Force visibility of Typography in first column cells */
            .print-area .MuiTableBody-root .MuiTableCell-root:first-child .MuiTypography-root,
            .print-area tbody td:first-child .MuiTypography-root {
              overflow: visible !important;
              white-space: normal !important;
              height: auto !important;
              max-height: none !important;
              word-wrap: break-word !important;
              line-height: 1.4 !important;
            }
            
            /* Specifically target subtitle2 (main title) in first column */
            .print-area .MuiTableBody-root .MuiTableCell-root:first-child .MuiTypography-subtitle2 {
              display: block !important;
              font-size: 8.5px !important;
              font-weight: bold !important;
              color: black !important;
              margin-bottom: 0.5mm !important;
              line-height: 1.3 !important;
            }
            
            /* Specifically target body2 (description) in first column */
            .print-area .MuiTableBody-root .MuiTableCell-root:first-child .MuiTypography-body2 {
              display: block !important;
              font-size: 7px !important;
              color: #555555 !important;
              line-height: 1.35 !important;
              margin-top: 0.5mm !important;
              overflow: visible !important;
              white-space: normal !important;
              word-wrap: break-word !important;
            }
            
            .print-header h4 {
              font-size: 18px !important;
              margin: 0 0 5mm 0 !important;
              text-align: center !important;
              font-weight: bold !important;
              color: black !important;
              background-color: white !important;
            }
            
            .print-header .MuiTypography-h6 {
              font-size: 12px !important;
              margin-bottom: 2mm !important;
              font-weight: 600 !important;
              color: black !important;
              background-color: white !important;
            }
            
            .print-header .MuiTypography-body2 {
              font-size: 9px !important;
              color: black !important;
              margin-top: 1mm !important;
              background-color: white !important;
            }
            
            .print-header .MuiBox-root {
              display: flex !important;
              justify-content: space-between !important;
              align-items: flex-start !important;
              background-color: white !important;
            }
            
            .section-title {
              font-size: 14px !important;
              margin: 8mm 0 4mm 0 !important;
              padding: 2mm 0 !important;
              border-bottom: 2px solid black !important;
              font-weight: bold !important;
              page-break-after: auto !important;
              page-break-before: auto !important;
              color: black !important;
            }
            
            .print-area .settlement-card {
              border: 2px solid #333 !important;
              margin-top: 3mm !important;
              margin-bottom: 5mm !important;
              padding: 3mm !important;
              background: white !important;
              page-break-inside: auto !important;
              border-radius: 3mm !important;
              width: 100% !important;
            }
            
            /* Make Net Settlement Position section more compact in print */
            .print-area .settlement-card h6 {
              font-size: 12px !important;
              margin-bottom: 2mm !important;
              font-weight: bold !important;
              color: black !important;
            }
            
            .print-area .settlement-card h3 {
              font-size: 22px !important;
              margin: 1mm 0 !important;
              font-weight: bold !important;
            }
            
            .print-area .settlement-card .MuiChip-root {
              font-size: 9px !important;
              height: 20px !important;
              font-weight: bold !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            
            /* Force chip background colors in print */
            .print-area .MuiChip-colorSuccess {
              background-color: #4caf50 !important;
              color: white !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            
            .print-area .MuiChip-colorError {
              background-color: #f44336 !important;
              color: white !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            
            .print-area .MuiChip-colorDefault {
              background-color: #e0e0e0 !important;
              color: #333 !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            
            .print-area .MuiChip-colorWarning {
              background-color: #ff9800 !important;
              color: white !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            
            .print-area .settlement-card .MuiTypography-body2 {
              font-size: 8px !important;
              margin-top: 0.5mm !important;
              color: #555 !important;
            }
            
            .print-area .settlement-card .MuiTypography-body1 {
              font-size: 9px !important;
              color: black !important;
              font-family: monospace !important;
              padding: 1mm !important;
              border: 1px dashed #999 !important;
              background-color: #f9f9f9 !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            
            .summary-cards {
              display: flex !important;
              gap: 2mm !important;
              margin-bottom: 2mm !important;
              flex-wrap: wrap !important;
              page-break-inside: auto !important;
            }
            
            .print-area .summary-card, .print-area .MuiCard-root {
              flex: 1 !important;
              min-width: 50mm !important;
              border: 1px solid black !important;
              padding: 1.5mm !important;
              background: white !important;
              text-align: center !important;
              color: black !important;
              border-radius: 0 !important;
              box-shadow: none !important;
              margin: 0 !important;
              page-break-inside: auto !important;
            }
            
            .print-area .MuiCardContent-root {
              padding: 1mm !important;
              page-break-inside: auto !important;
            }
            
            .summary-card h6 {
              font-size: 10px !important;
              margin-bottom: 1mm !important;
              font-weight: bold !important;
              color: black !important;
            }
            
            .summary-card .amount {
              font-size: 14px !important;
              font-weight: bold !important;
              margin-bottom: 1mm !important;
              color: black !important;
            }
            
            .summary-card .description {
              font-size: 8px !important;
              color: black !important;
            }
            
            .breakdown-section {
              margin-bottom: 4mm !important;
              page-break-inside: auto !important;
              break-inside: auto !important;
              border: none !important;
              padding: 0 !important;
              background: white !important;
            }
            
            /* Section headers with full width styling */
            .print-area .section-header {
              background-color: #f0f0f0 !important;
              padding: 2mm !important;
              margin: -2mm -2mm 2mm -2mm !important;
              border-bottom: 2px solid black !important;
              font-weight: bold !important;
              text-align: center !important;
              font-size: 10px !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            
            /* Remove last cell border for cleaner look */
            .print-area .MuiTableCell-root:last-child,
            .print-area td:last-child,
            .print-area th:last-child {
              border-right: none !important;
            }
            
            /* First column styling */
            .print-area .MuiTableCell-root:first-child,
            .print-area td:first-child,
            .print-area th:first-child {
              border-left: none !important;
            }
            
            .print-area .section-number {
              display: inline-block !important;
              width: 20px !important;
              height: 20px !important;
              background-color: #333 !important;
              color: white !important;
              border-radius: 50% !important;
              text-align: center !important;
              line-height: 20px !important;
              font-size: 12px !important;
              font-weight: bold !important;
              margin-right: 8px !important;
            }
            
            .print-area .section-title-with-number {
              display: flex !important;
              align-items: center !important;
              margin-bottom: 2mm !important;
              padding-bottom: 1mm !important;
              border-bottom: 1px solid black !important;
            }
            
            .print-area .MuiTable-root, .print-area table {
              width: 100% !important;
              border-collapse: collapse !important; /* Changed to collapse to eliminate gaps */
              margin-bottom: 3mm !important;
              font-size: 9px !important;
              page-break-inside: auto !important;
              page-break-before: auto !important;
              page-break-after: auto !important;
              color: black !important;
              background: white !important;
              border: 1px solid #cccccc !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            
            /* Ensure table rows display properly with multi-line content */
            .print-area .MuiTableRow-root, .print-area tr {
              height: auto !important;
              page-break-inside: avoid !important;
            }
            
            .print-area .MuiTableBody-root .MuiTableRow-root, .print-area tbody tr {
              height: auto !important;
              min-height: 0 !important;
            }
            
            .print-area .MuiTableHead-root th, .print-area th {
              background-color: #f5f5f5 !important;
              font-weight: bold !important;
              padding: 2mm 1mm !important;
              border: 1px solid #cccccc !important;
              border-bottom: 2px solid #cccccc !important;
              text-align: center !important;
              font-size: 8px !important;
              color: black !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            
            /* First column header and cell need left border */
            .print-area tr th:first-child,
            .print-area tr td:first-child {
              border-left: 1px solid #cccccc !important;
            }
            
            /* Ensure all colors are properly set for print regardless of dark mode */
            @media print {
              .print-area * {
                color-adjust: exact !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              
              .print-area .MuiChip-root {
                color: white !important;
                print-color-adjust: exact !important;
              }
            }
            
            .print-area .MuiTableCell-root, .print-area td {
              padding: 1.5mm 1mm !important;
              border: 1px solid #cccccc !important;
              font-size: 8px !important;
              color: black !important;
              background: white !important;
              text-align: left !important;
              vertical-align: top !important;
              line-height: 1.3 !important;
            }
            
            /* Net Settlement rows styling for print */
            .print-area tr.net-settlement-row td,
            .print-area .MuiTableBody-root .net-settlement-row .MuiTableCell-root {
              background-color: #f0f0f0 !important;
              border-top: 2px solid #cccccc !important;
              font-weight: bold !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            
            /* Ensure net settlement row descriptions are visible */
            .print-area tr.net-settlement-row td:first-child,
            .print-area .MuiTableBody-root .net-settlement-row .MuiTableCell-root:first-child {
              padding: 2.5mm 1.5mm !important;
            }
            
            .print-area tr.net-settlement-row .MuiTypography-subtitle2 {
              font-size: 9px !important;
              font-weight: bold !important;
              color: black !important;
            }
            
            .print-area tr.net-settlement-row .MuiTypography-body2 {
              font-size: 7px !important;
              color: #444444 !important;
              font-weight: normal !important;
            }
            
            /* Ensure chips in net settlement rows are visible in print */
            .print-area tr.net-settlement-row .MuiChip-root {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color: white !important;
            }
            
            .print-area tr.net-settlement-row .MuiChip-colorError {
              background-color: #f44336 !important;
            }
            
            .print-area tr.net-settlement-row .MuiChip-colorSuccess {
              background-color: #4caf50 !important;
            }
            
            .print-table .number {
              text-align: right !important;
            }
            
            .settlement-summary {
              border: none !important;
              padding: 0 !important;
              margin: 4mm 0 !important;
              background: white !important;
              page-break-inside: auto !important;
            }
            
            .settlement-grid {
              display: grid !important;
              grid-template-columns: 1fr 1fr !important;
              gap: 4mm !important;
              margin-bottom: 4mm !important;
            }
            
            .settlement-item {
              border: 1px solid black !important;
              padding: 3mm !important;
              background: white !important;
              color: black !important;
            }
            
            .settlement-item-title {
              font-size: 10px !important;
              font-weight: bold !important;
              margin-bottom: 2mm !important;
              color: black !important;
            }
            
            .settlement-amount {
              font-size: 14px !important;
              font-weight: bold !important;
              margin-bottom: 2mm !important;
              color: black !important;
            }
            
            .settlement-description {
              font-size: 8px !important;
              color: black !important;
            }
            
            .net-position {
              text-align: center !important;
              padding: 4mm !important;
              border: 2px solid black !important;
              background: #f5f5f5 !important;
              margin-top: 4mm !important;
            }
            
            .net-amount {
              font-size: 18px !important;
              font-weight: bold !important;
              color: black !important;
            }
            
            .page-footer {
              margin-top: 10mm !important;
              font-size: 8px !important;
              text-align: center !important;
              border-top: 1px solid #ccc !important;
              padding-top: 3mm !important;
            }
            
            /* PDF Page Break Controls */
            .print-section {
              page-break-inside: auto !important;
              margin-bottom: 4mm !important;
              break-inside: auto !important;
              orphans: 2 !important;
              widows: 2 !important;
            }
            
            /* Force page breaks between major sections in PDF */
            .print-area .cash-orders-breakdown {
              page-break-after: auto !important;
              page-break-inside: auto !important;
              break-inside: auto !important;
              orphans: 3 !important;
              widows: 3 !important;
            }
            
            .print-area .online-orders-breakdown {
              page-break-before: auto !important;
              page-break-after: auto !important;
              page-break-inside: auto !important;
              break-inside: auto !important;
              orphans: 3 !important;
              widows: 3 !important;
            }
            
            .print-area .settlement-summary-section {
              page-break-before: auto !important;
              page-break-inside: auto !important;
              break-inside: auto !important;
              orphans: 2 !important;
              widows: 2 !important;
            }
            
            /* Allow tables to break across pages naturally */
            .print-area .MuiTable-root {
              page-break-inside: auto !important;
              break-inside: auto !important;
            }
            
            .print-area .MuiTableRow-root {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            
            /* Allow content to flow naturally across pages */
            .print-area .MuiCardContent-root,
            .print-area .MuiBox-root {
              page-break-inside: auto !important;
            }
            
            /* Ensure PDF respects page boundaries */
            .print-area .section-content {
              page-break-inside: auto !important;
            }
          }
        `}
      </style>

      {/* Header Section - Print Button Only */}
      <Box sx={{ p: 2, pb: 1, display: 'flex', justifyContent: 'flex-end' }} className="no-print">
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          size="small"
        >
          Print Report
        </Button>
      </Box>

      {/* Report Content Container */}
      <div className="report-container">
        {/* Report Header Info - Centered with Restaurant in Header */}
        <Box sx={{ mb: 3 }} className="print-header">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }} className="app-name">
              Dining-x
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.text.primary }}>
              {report.restaurantInfo.restaurantName}
            </Typography>
          </Box>
          <Typography variant="h4" sx={{ textAlign: 'center', mb: 2, fontWeight: 'bold', color: theme.palette.primary.main }}>
            Financial Settlement Report
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
            <span><strong>Period:</strong> {formatDate(report.periodStart)} - {formatDate(report.periodEnd)}</span>
            {selectedBranchName && <span style={{ marginLeft: '20px' }}><strong>Branch:</strong> {selectedBranchName}</span>}
          </Typography>
        </Box>

        {/* Net Settlement Position - Enhanced Layout */}
        <div className="settlement-card print-section" style={{backgroundColor: theme.palette.mode === 'dark' ? 'transparent' : theme.palette.background.paper}}>
          <div className="settlement-content">
            <Box sx={{ 
              borderBottom: `1px solid ${theme.palette.divider}`, 
              pb: 1, 
              mb: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Typography variant="h6" sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                fontWeight: 'bold',
                color: theme.palette.primary.main
              }}>
                <SwapHorizIcon sx={{ mr: 1 }} />
                Net Settlement Position
              </Typography>
              <Chip
                label={
                  settlementSummary.netSettlementPosition.settlementDirection === 'talabat_pays_restaurant'
                    ? 'Talabat pays Restaurant'
                    : settlementSummary.netSettlementPosition.settlementDirection === 'restaurant_pays_talabat'
                    ? 'Restaurant pays Talabat'
                    : 'No Settlement Needed'
                }
                color={
                  settlementSummary.netSettlementPosition.settlementDirection === 'talabat_pays_restaurant'
                    ? 'success'
                    : settlementSummary.netSettlementPosition.settlementDirection === 'restaurant_pays_talabat'
                    ? 'error'
                    : 'default'
                }
                sx={{ fontSize: '0.875rem', px: 1.5, fontWeight: 'bold' }}
                onClick={(e) => e.preventDefault()}
              />
            </Box>
            
            <Box sx={{ 
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              justifyContent: 'center',
              gap: 3,
              p: 2,
              backgroundColor: settlementSummary.netSettlementPosition.settlementDirection === 'talabat_pays_restaurant'
                ? alpha(theme.palette.success.light, 0.1)
                : settlementSummary.netSettlementPosition.settlementDirection === 'restaurant_pays_talabat'
                ? alpha(theme.palette.error.light, 0.1)
                : alpha(theme.palette.grey[200], 0.5),
              borderRadius: 1,
            }}>
              <Box sx={{ 
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <Typography variant="body2" color="text.secondary" fontWeight="medium">
                  Settlement Amount
                </Typography>
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontWeight: 'bold',
                    color: settlementSummary.netSettlementPosition.settlementDirection === 'talabat_pays_restaurant'
                      ? theme.palette.success.main 
                      : settlementSummary.netSettlementPosition.settlementDirection === 'restaurant_pays_talabat'
                      ? theme.palette.error.main
                      : theme.palette.text.primary
                  }}
                >
                  {formatCurrency(Math.abs(settlementSummary.netSettlementPosition.netSettlementAmount))}
                </Typography>
              </Box>
              
              <Box sx={{ 
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                maxWidth: '400px'
              }}>
                <Typography variant="body2" color="text.secondary" fontWeight="medium">
                  Calculation Details
                </Typography>
                <Typography variant="body1" sx={{ 
                  textAlign: 'center', 
                  fontFamily: 'monospace',
                  backgroundColor: alpha(theme.palette.background.default, 0.7),
                  p: 1,
                  borderRadius: 1,
                  border: `1px dashed ${theme.palette.divider}`
                }}>
                  {settlementSummary.netSettlementPosition.calculation}
                </Typography>
              </Box>
            </Box>
          </div>
        </div>
      
        {/* Cash Orders Breakdown */}
        <div className="breakdown-section cash-orders-breakdown print-section">
          <div className="section-content">
            <div className="section-title-with-number">
              <span className="section-number">1</span>
              <Typography variant="h6" sx={{ margin: 0 }}>
                Cash Orders Breakdown
              </Typography>
            </div>
            
            <Table className="print-table" size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Category</strong></TableCell>
                    <TableCell align="right"><strong>Count/Rate</strong></TableCell>
                    <TableCell align="right"><strong>Amount</strong></TableCell>
                    <TableCell><strong>Settlement</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* 1. Total Orders - Foundation */}
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Total Cash Orders</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Delivered order value (excluding discounts, cancelled order after closed)
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(cashOrdersBreakdown.totalCashOrdersSummary.orderCount)} orders
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {formatCurrency(cashOrdersBreakdown.totalCashOrdersSummary.totalOrderValue)}
                    </TableCell>
                    <TableCell>Base calculation</TableCell>
                  </TableRow>
                  
                  {/* 2. Commission - What restaurant owes */}
                  <TableRow>
                    <TableCell>
                      <Typography variant="subtitle2">Talabat Commission</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatPercentage(cashOrdersBreakdown.talabatCommission.commissionRate)} of total orders
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {formatPercentage(cashOrdersBreakdown.talabatCommission.commissionRate)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: theme.palette.error.main }}>
                      -{formatCurrency(cashOrdersBreakdown.talabatCommission.commissionAmount)}
                    </TableCell>
                    <TableCell>
                      <Chip label="Restaurant owes" color="error" size="small" onClick={(e) => e.preventDefault()} />
                    </TableCell>
                  </TableRow>
                  
                  {/* 3. Discounts - What Talabat owes back */}
                  <TableRow>
                    <TableCell>
                      <Typography variant="subtitle2">Discount Reimbursements</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Talabat promotional discounts (excluded from total order value)
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(cashOrdersBreakdown.cashDiscounts.discountedOrderCount)} orders
                    </TableCell>
                    <TableCell align="right" sx={{ color: theme.palette.success.main }}>
                      +{formatCurrency(cashOrdersBreakdown.cashDiscounts.totalDiscountAmount)}
                    </TableCell>
                    <TableCell>
                      <Chip label="Talabat owes" color="success" size="small" onClick={(e) => e.preventDefault()} />
                    </TableCell>
                  </TableRow>
                  
                  {/* 4. Cash Cancellation Penalties - What restaurant owes */}
                  <TableRow sx={{ 
                    backgroundColor: cashOrdersBreakdown.cancellations.cancelledCount > 0 ? 'inherit' : '#fafafa',
                    opacity: cashOrdersBreakdown.cancellations.cancelledCount > 0 ? 1 : 0.7
                  }}>
                    <TableCell>
                      <Typography variant="subtitle2">Cancellation Penalties</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatPercentage(cashOrdersBreakdown.cancellations.penaltyRate)} penalty on {formatCurrency(cashOrdersBreakdown.cancellations.totalCancelledValue)} ({formatNumber(cashOrdersBreakdown.cancellations.cancelledCount)} cancelled after closed orders)
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(cashOrdersBreakdown.cancellations.cancelledCount)} orders
                    </TableCell>
                    <TableCell align="right" sx={{ 
                      color: cashOrdersBreakdown.cancellations.penaltyAmount > 0 ? theme.palette.error.main : theme.palette.text.secondary 
                    }}>
                      {cashOrdersBreakdown.cancellations.penaltyAmount > 0 ? '-' : ''}{formatCurrency(cashOrdersBreakdown.cancellations.penaltyAmount)}
                    </TableCell>
                    <TableCell>
                      {cashOrdersBreakdown.cancellations.penaltyAmount > 0 ? (
                        <Chip label="Restaurant owes" color="error" size="small" onClick={(e) => e.preventDefault()} />
                      ) : (
                        <Chip label="No penalty" color="default" size="small" onClick={(e) => e.preventDefault()} />
                      )}
                    </TableCell>
                  </TableRow>
                  
                  {/* 5. Cash Settlement Total */}
                  <TableRow 
                    className="net-settlement-row"
                    sx={{ 
                      backgroundColor: '#f0f0f0 !important',
                      borderTop: `2px solid ${theme.palette.divider} !important`,
                      '& .MuiTableCell-root': {
                        backgroundColor: '#f0f0f0 !important',
                        borderTop: `2px solid ${theme.palette.divider} !important`,
                      }
                    }}>
                    <TableCell sx={{ backgroundColor: '#f0f0f0 !important' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Cash Orders Net Settlement</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Commission + Penalties - Discount Reimbursements
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ backgroundColor: '#f0f0f0 !important' }}>
                      Net total
                    </TableCell>
                    <TableCell align="right" sx={{ 
                      fontWeight: 'bold', 
                      backgroundColor: '#f0f0f0 !important',
                      color: (cashOrdersBreakdown.talabatCommission.commissionAmount + 
                        cashOrdersBreakdown.cancellations.penaltyAmount - 
                        cashOrdersBreakdown.cashDiscounts.totalDiscountAmount) > 0 
                        ? theme.palette.error.main : theme.palette.success.main
                    }}>
                      {formatCurrency(
                        cashOrdersBreakdown.talabatCommission.commissionAmount + 
                        cashOrdersBreakdown.cancellations.penaltyAmount - 
                        cashOrdersBreakdown.cashDiscounts.totalDiscountAmount
                      )}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f0f0f0 !important' }}>
                      {(cashOrdersBreakdown.talabatCommission.commissionAmount + 
                        cashOrdersBreakdown.cancellations.penaltyAmount - 
                        cashOrdersBreakdown.cashDiscounts.totalDiscountAmount) > 0 
                        ? <Chip label="Restaurant owes" color="error" size="small" onClick={(e) => e.preventDefault()} />
                        : <Chip label="Talabat owes" color="success" size="small" onClick={(e) => e.preventDefault()} />
                      }
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
          </div>
        </div>

        {/* Online Orders Breakdown */}
        <div className="breakdown-section online-orders-breakdown print-section">
          <div className="section-content">
            <div className="section-title-with-number">
              <span className="section-number">2</span>
              <Typography variant="h6" sx={{ margin: 0 }}>
                Online Orders Breakdown
              </Typography>
            </div>
            
            <Table className="print-table" size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Category</strong></TableCell>
                    <TableCell align="right"><strong>Count/Rate</strong></TableCell>
                    <TableCell align="right"><strong>Amount</strong></TableCell>
                    <TableCell><strong>Settlement</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* 1. Total Orders - Foundation */}
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Total Online Orders</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Delivered order value (excluding discounts, cancelled order after closed)
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(onlineOrdersBreakdown.totalOnlineOrdersSummary.orderCount)} orders
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {formatCurrency(onlineOrdersBreakdown.totalOnlineOrdersSummary.totalOrderValue)}
                    </TableCell>
                    <TableCell>Base calculation</TableCell>
                  </TableRow>
                  
                  {/* 2. Commission - What Talabat keeps */}
                  <TableRow>
                    <TableCell>
                      <Typography variant="subtitle2">Talabat Commission</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatPercentage(onlineOrdersBreakdown.talabatCommission.commissionRate)} of total orders
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {formatPercentage(onlineOrdersBreakdown.talabatCommission.commissionRate)}
                    </TableCell>
                    <TableCell align="right">
                      -{formatCurrency(onlineOrdersBreakdown.talabatCommission.commissionAmount)}
                    </TableCell>
                    <TableCell>Talabat keeps</TableCell>
                  </TableRow>
                  
                  {/* 3. Restaurant Share - What restaurant gets */}
                  <TableRow>
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Restaurant Share</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatPercentage(onlineOrdersBreakdown.restaurantShare.shareRate)} after commission
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      Net share
                    </TableCell>
                    <TableCell align="right" sx={{ color: theme.palette.success.main, fontWeight: 'bold' }}>
                      +{formatCurrency(onlineOrdersBreakdown.restaurantShare.netAmount)}
                    </TableCell>
                    <TableCell>
                      <Chip label="Talabat owes" color="success" size="small" onClick={(e) => e.preventDefault()} />
                    </TableCell>
                  </TableRow>
                  
                  {/* 4. Discounts - For review only */}
                  <TableRow sx={{ backgroundColor: '#fafafa', opacity: 0.7 }}>
                    <TableCell>
                      <Typography variant="subtitle2">Discount Reimbursements</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Promotional discounts (excluded from total order value, for review only)
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(onlineOrdersBreakdown.onlineDiscounts.discountedOrderCount)} orders
                    </TableCell>
                    <TableCell align="right" sx={{ color: theme.palette.text.secondary }}>
                      {formatCurrency(onlineOrdersBreakdown.onlineDiscounts.totalDiscountAmount)}
                    </TableCell>
                    <TableCell>
                      <Chip label="For review only" color="default" size="small" onClick={(e) => e.preventDefault()} />
                    </TableCell>
                  </TableRow>
                  
                  {/* 5. Online Cancellation Penalties - What Talabat owes */}
                  <TableRow sx={{ 
                    backgroundColor: onlineOrdersBreakdown.cancellations.cancelledCount > 0 ? 'inherit' : '#fafafa',
                    opacity: onlineOrdersBreakdown.cancellations.cancelledCount > 0 ? 1 : 0.7
                  }}>
                    <TableCell>
                      <Typography variant="subtitle2">Cancellation Penalties</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatPercentage(onlineOrdersBreakdown.cancellations.penaltyRate)} penalty on {formatCurrency(onlineOrdersBreakdown.cancellations.totalCancelledValue)} ({formatNumber(onlineOrdersBreakdown.cancellations.cancelledCount)} cancelled after closed orders)
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(onlineOrdersBreakdown.cancellations.cancelledCount)} orders
                    </TableCell>
                    <TableCell align="right" sx={{ 
                      color: onlineOrdersBreakdown.cancellations.penaltyAmount > 0 ? theme.palette.success.main : theme.palette.text.secondary 
                    }}>
                      {onlineOrdersBreakdown.cancellations.penaltyAmount > 0 ? '+' : ''}{formatCurrency(onlineOrdersBreakdown.cancellations.penaltyAmount)}
                    </TableCell>
                    <TableCell>
                      {onlineOrdersBreakdown.cancellations.penaltyAmount > 0 ? (
                        <Chip label="Talabat owes" color="success" size="small" onClick={(e) => e.preventDefault()} />
                      ) : (
                        <Chip label="No penalty" color="default" size="small" onClick={(e) => e.preventDefault()} />
                      )}
                    </TableCell>
                  </TableRow>
                  
                  {/* 6. Online Settlement Total */}
                  <TableRow 
                    className="net-settlement-row"
                    sx={{ 
                      backgroundColor: '#f0f0f0 !important',
                      borderTop: `2px solid ${theme.palette.divider} !important`,
                      '& .MuiTableCell-root': {
                        backgroundColor: '#f0f0f0 !important',
                        borderTop: `2px solid ${theme.palette.divider} !important`,
                      }
                    }}>
                    <TableCell sx={{ backgroundColor: '#f0f0f0 !important' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Online Orders Net Settlement</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Restaurant Share + Discount Reimbursements + Penalties
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ backgroundColor: '#f0f0f0 !important' }}>
                      Net total
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#f0f0f0 !important', color: theme.palette.success.main }}>
                      {formatCurrency(
                        onlineOrdersBreakdown.restaurantShare.netAmount +  
                        onlineOrdersBreakdown.cancellations.penaltyAmount
                      )}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f0f0f0 !important' }}>
                      {(onlineOrdersBreakdown.restaurantShare.netAmount +  
                        onlineOrdersBreakdown.cancellations.penaltyAmount) > 0 
                        ? <Chip label="Talabat owes" color="success" size="small" onClick={(e) => e.preventDefault()} />
                        : <Chip label="Restaurant owes" color="error" size="small" onClick={(e) => e.preventDefault()} />
                      }
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
          </div>
        </div>

        {/* Money Already Properly Allocated */}
        <div className="settlement-summary settlement-summary-section print-section">
          <div className="section-content">
            <Typography variant="h6" gutterBottom className="breakdown-title">
              Money Already Properly Allocated (No Settlement Needed)
            </Typography>
            
            <Table className="print-table" size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Category</strong></TableCell>
                    <TableCell align="right"><strong>Amount</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Online Commissions (Talabat)</TableCell>
                    <TableCell align="right">
                      {formatCurrency(settlementSummary.moneyAlreadyProperlyAllocated.onlineCommissions)}
                    </TableCell>
                    <TableCell>Already with Talabat</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Cash Received (Restaurant)</TableCell>
                    <TableCell align="right">
                      {formatCurrency(settlementSummary.moneyAlreadyProperlyAllocated.cashReceivedByRestaurant)}
                    </TableCell>
                    <TableCell>Already with Restaurant</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialSettlementReport;